import React from 'react'
import { Box, Link } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import ErrorCircleIcon from '@src/icons/ErrorCircleIcon'
import { Error } from '@mui/icons-material'
import messages from '@src/utils/messages'
import { FormattedMessage } from 'react-intl'
import GlobalAlert from '../GlobalAlert'
import { useRouter } from 'next/router'
import { DiscontinuedMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import { TrackedAvailabilityMessage } from '../TrackedAvailabilityMessage/TrackedAvailabilityMessage'
import { support } from '@src/routes'

const useStyles = makeStyles()((theme) => ({
  simpleAlert: {
    display: 'flex',
    justifyContent: 'center',
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('lg')]: {
      display: 'inline',
    },
  },
  errorIcon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('lg')]: {
      display: 'inline',
      position: 'relative',
      top: theme.spacing(1),
    },
  },
  alignLeft: {
    justifyContent: 'flex-start',
  },
  errorAlert: {
    display: 'flex',
    alignItems: 'flex-start',
    lineHeight: 1.5,
  },
  link: {
    cursor: 'pointer',
    margin: theme.spacing(0, 1),
  },
}))

interface DiscontinuedProductAlertProps {
  discontinuedPricingInfo?: DiscontinuedMaterialPricingDetailFragment | null
  leftAligned?: boolean
}

const DiscontinuedProductAlert: React.FC<DiscontinuedProductAlertProps> = ({
  discontinuedPricingInfo,
  leftAligned,
}) => {
  const { classes, cx } = useStyles()
  const router = useRouter()
  const srpPage = router.asPath.includes('/search')
  const comparePage = router.asPath.includes('/compare')

  const showReplacementProducts =
    !!discontinuedPricingInfo?.replacementProducts?.length ||
    !!discontinuedPricingInfo?.displaySimilarProductLabel ||
    !discontinuedPricingInfo?.hideReplacementProductLink

  const recommendedMsg = discontinuedPricingInfo?.displaySimilarProductLabel ? (
    <>
      <FormattedMessage
        id="RECOMMENDED_MESSAGE_DISCRIPTION"
        defaultMessage="View Similar Product(s) Below or{contactTechnicalService}for assistance. "
        values={{
          contactTechnicalService: (
            <Link
              className={classes.link}
              href={support.customerSupportWithFormId('tfa_13')}
            >
              <FormattedMessage {...messages.CONTACT_TECHNICAL_SERVICE} />
            </Link>
          ),
        }}
      />
    </>
  ) : (
    <FormattedMessage {...messages.RECOMMENDED_REPLACEMENT_PRODUCTS} />
  )

  const alertMessage = showReplacementProducts ? (
    <TrackedAvailabilityMessage
      {...messages.THIS_PRODUCT_DISCONTINUED}
      values={{
        recommendedProductMessage: !comparePage ? recommendedMsg : null,
      }}
    />
  ) : (
    <TrackedAvailabilityMessage
      {...messages.THIS_PRODUCT_DISCONTINUED}
      values={{
        recommendedProductMessage: (
          <>
            <Link
              className={classes.link}
              href={support.customerSupportWithFormId('tfa_13')}
            >
              <FormattedMessage {...messages.CONTACT_TECHNICAL_SERVICE} />
            </Link>
            <FormattedMessage {...messages.FOR_ASSISTANCE} />
          </>
        ),
      }}
    />
  )

  return srpPage || comparePage ? (
    <Box
      className={cx(classes.simpleAlert, {
        [classes.alignLeft]: comparePage || leftAligned,
      })}
    >
      <Box className={classes.errorIcon}>
        <Error color="error" />
      </Box>
      {alertMessage}
    </Box>
  ) : (
    <GlobalAlert
      severity="error"
      className={classes.errorAlert}
      icon={<ErrorCircleIcon style={{ fontSize: 16 }} />}
    >
      {alertMessage}
    </GlobalAlert>
  )
}

export default DiscontinuedProductAlert
